import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-4972">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4973">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-4974">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-4975">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-4976" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-4977">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4978" />
              <p className="text-sm text-gray-600" data-id="element-4979">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-4980">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-4981" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-4982">
          {/* Quality & Metadata */}
          <div data-id="element-4983">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4984">
              <Brain className="w-3.5 h-3.5" data-id="element-4985" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-4986">
              <div className="grid grid-cols-2 gap-4" data-id="element-4987">
                <div data-id="element-4988">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4989">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-4990">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-4991">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4992">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-4993">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-4994">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-4995">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-4996">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4997">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4998">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-4999">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5000">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5001">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-5002">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5003">
              <Database className="w-3.5 h-3.5" data-id="element-5004" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-5005">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5006" />
                <span className="text-sm text-gray-600" data-id="element-5007">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-5008">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-5009">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-5010">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5011">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-5012" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-5013">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-5014">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5015" />
                  <span className="text-sm" data-id="element-5016">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-5017">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5018" />
                  <span className="text-sm" data-id="element-5019">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-5020">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5021">
              <BookOpen className="w-3.5 h-3.5" data-id="element-5022" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-5023">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5024">
                      <div data-id="element-5025">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5026">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5027">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-5028">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-5029">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5030">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5031">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-5032">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-5033">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-5034">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-5035">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5036" />
                        <span className="text-sm text-gray-600" data-id="element-5037">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-5038">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-5039" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-5040" />}
                        <span className="text-sm text-gray-600" data-id="element-5041">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}